import { useEffect, useState } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { api, session } from './lib/api'
import { startSyncLoop } from './lib/outbox'
import { Login } from './Login'
import { ConsoleShell } from './console/ConsoleShell'
import { FieldShell } from './field/FieldShell'
import { FarmerShell } from './farmer/FarmerShell'
import { BuyerShell } from './buyer/BuyerShell'

/**
 * Which shell a person lands in is decided by their role, not by the URL they typed.
 * A field agent opening the root gets the field app; ops gets the console. Either can
 * still navigate to the other if their roles allow it.
 */
function homeFor(roles: string[]) {
  if (roles.some((r) => ['field_agent', 'depot_clerk', 'driver'].includes(r)) &&
      !roles.some((r) => ['admin', 'ops', 'finance', 'sales'].includes(r))) {
    return '/m'
  }
  return '/console'
}

export function App() {
  const [ready, setReady] = useState(false)
  const [signedIn, setSignedIn] = useState(session.isSignedIn)

  useEffect(() => {
    // Restore the in-memory access token after a reload before rendering anything
    // that would immediately 401.
    void api.resume().finally(() => setReady(true))
  }, [])

  useEffect(() => {
    if (!signedIn) return
    return startSyncLoop()
  }, [signedIn])

  if (!ready) {
    return (
      <div className="empty" style={{ height: '100%', justifyContent: 'center' }}>
        <b>Ledger</b>
      </div>
    )
  }

  const roles = session.user?.roles ?? []

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/login"
          element={
            signedIn ? <Navigate to={homeFor(roles)} replace /> : <Login onSignedIn={() => setSignedIn(true)} />
          }
        />
        <Route
          path="/login/:door"
          element={
            signedIn ? <Navigate to={homeFor(roles)} replace /> : <Login onSignedIn={() => setSignedIn(true)} />
          }
        />
        <Route
          path="/console/*"
          element={signedIn ? <ConsoleShell onSignOut={() => setSignedIn(false)} /> : <Navigate to="/login/office" replace />}
        />
        <Route
          path="/m/*"
          element={signedIn ? <FieldShell onSignOut={() => setSignedIn(false)} /> : <Navigate to="/login/field" replace />}
        />
        {/* The farmer app has its own sign-in and its own session, so it sits outside
            the staff auth gate entirely. A farmer and an agent may share a phone. */}
        <Route path="/f/*" element={<FarmerShell />} />
        {/* And the buyer's, for the same reason: their own session, their own door. */}
        <Route path="/b/*" element={<BuyerShell />} />
        <Route path="*" element={<Navigate to={signedIn ? homeFor(roles) : '/login'} replace />} />
      </Routes>
    </BrowserRouter>
  )
}
